import { BalanceData, BossMechanic } from "../data/balance";
import { EmberSource, LiveEventData } from "../data/events";

/**
 * What earns an event's embers (MP-21, events.json `_sources`): each event lists its sources as data —
 * {signal, amount, per, runCap, cap} — and the client reports signals where they happen (a battle's end, a Daily Order
 * claimed, an upgrade completed, Tribute collected). These are pure: what a count of signals pays an event; the caps
 * across the event live with the progress (meta/events.ts EventProgress).
 */

/** Every signal the client produces (gen.py EMBER_SIGNALS holds the same list). */
export const EMBER_SIGNALS = [
  // a battle's end
  "win", "loss", "lossStage", "modifiedWin", "heroicWin", "trialWin", "bossWin", "star", "earlyCall", "noLeakWin",
  "kill", "featuredKill", "eliteKill", "rareKill",
  // the hub
  "order", "upgrade", "tribute",
  // MP-20's Daily Road (named for that package; it reports a finished road run)
  "dailyRoad",
] as const;
export type EmberSignal = (typeof EMBER_SIGNALS)[number];

/** Signal counts from one occurrence (a battle, a hub sync): signal → how many times. */
export type SignalCounts = Readonly<Partial<Record<EmberSignal, number>>>;

/** The kills per `amount` a source counts: the featured enemy's own rate for featuredKill, else the source's `per`. */
export function perOf(e: LiveEventData, s: EmberSource): number {
  return s.signal === "featuredKill" && e.featured ? e.featured.per : s.per;
}

/**
 * Embers `n` of a source's signal pay in one occurrence: floor(n / per) × amount, at most runCap, and no more than the
 * source's event-wide cap leaves after `earned` (0 = no limit for either).
 */
export function sourcePays(s: EmberSource, n: number, per: number, earned: number): number {
  if (!(n > 0)) return 0;
  let pay = Math.floor(n / Math.max(1, per)) * s.amount;
  if (s.runCap > 0) pay = Math.min(pay, s.runCap);
  if (s.cap > 0) pay = Math.min(pay, s.cap - earned);
  return Math.max(0, pay);
}

/** Embers `signals` pay the event before any event-wide cap is reached (a preview: EventProgress applies the caps). */
export function embersFor(e: LiveEventData, signals: SignalCounts): number {
  return e.sources.reduce((sum, s) => sum + sourcePays(s, signals[s.signal as EmberSignal] ?? 0, perOf(e, s), 0), 0);
}

/** A finished battle as its end screen knows it. `counts` are the battle's tally (meta/orderProgress.ts BattleTally keys). */
export interface EmberBattle {
  readonly level: number;
  readonly won: boolean;
  readonly stagesCleared: number;
  /** A Heroic or Weekly Trial run, else null. */
  readonly kind: "heroic" | "trial" | null;
  /** Stars the run earned (0 on a loss). */
  readonly stars: number;
  readonly leaks: number;
  readonly counts: Readonly<Record<string, number>>;
  /** Rares killed (sim.rareRuns). */
  readonly rareKills: number;
}

const ELITE_KILLS = "kills:elite:";

/** Whether `level` ends on a signature boss (a boss mechanic: the Warlord's levels 7 and 10, every level from 11). */
export function signatureBossLevel(balance: BalanceData, level: number): boolean {
  if (level < 1 || level > balance.levels.length) return false;
  return balance.enemies[balance.level(level).bossEnemyIndex]?.bossMechanic !== BossMechanic.None;
}

/** The signals a finished battle reports to the live event `e` (its featured enemy names the featuredKill count). */
export function battleSignals(balance: BalanceData, e: LiveEventData | null, b: EmberBattle): SignalCounts {
  const w = b.won ? 1 : 0, c = b.counts;
  const elites = Object.keys(c).filter((k) => k.startsWith(ELITE_KILLS)).reduce((s, k) => s + (c[k] ?? 0), 0);
  return {
    win: w, loss: 1 - w, lossStage: b.won ? 0 : Math.max(0, b.stagesCleared),
    modifiedWin: w && b.kind !== null ? 1 : 0, heroicWin: w && b.kind === "heroic" ? 1 : 0, trialWin: w && b.kind === "trial" ? 1 : 0,
    bossWin: w && signatureBossLevel(balance, b.level) ? 1 : 0, star: b.won ? Math.max(0, b.stars) : 0,
    earlyCall: c.wavesCalled ?? 0, noLeakWin: w && b.leaks === 0 ? 1 : 0,
    kill: c["kills:any"] ?? 0, featuredKill: e?.featured ? c[`kills:${e.featured.enemy}`] ?? 0 : 0, eliteKill: elites, rareKill: Math.max(0, b.rareKills),
  };
}
